import * as THREE from 'three'
import { FeatureTopologyDisplayEdgesResourceFactory } from '../../../mesh/feature'

/**
 * 使用 occurrence 实例矩阵绘制 imported-model 默认拓扑边线的 LineSegments。
 *
 * Three.js 没有公开的 InstancedLineSegments 类；WebGPU/NodeMaterial 会在对象声明
 * `isInstancedMesh` 且提供 InstancedBufferAttribute 时应用标准实例矩阵，同时对象仍以
 * LineSegments 的 line-list primitive 渲染。该对象不参与拾取，拓扑选择继续由语义宿主负责。
 */
export class ImportedModelInstancedTopologyDisplayEdges extends THREE.LineSegments {
  /** 让 Three.js 标准材质节点启用 instanceMatrix 变换。 */
  public readonly isInstancedMesh = true

  /** 与表面 InstancedMesh 共用的 GPU 实例矩阵属性。 */
  public readonly instanceMatrix: THREE.InstancedBufferAttribute

  /** 当前实例数量。 */
  public readonly count: number

  /** 与 Three.js InstancedMesh 运行时契约对齐；边线不使用实例颜色。 */
  public readonly instanceColor = null

  /** 与 Three.js InstancedMesh 运行时契约对齐；边线不使用 morph texture。 */
  public readonly morphTexture = null

  /** 当前对象是否已经释放。 */
  private disposed = false

  /**
   * 创建实例化拓扑显示边。
   *
   * @param snapshots 当前表面槽位对应的去重拓扑快照。
   * @param color 默认显示边颜色。
   * @param count occurrence 数量。
   * @param instanceMatrix 与正式表面共享的实例矩阵属性。
   */
  public constructor(
    snapshots: readonly TopologySnapshot[],
    color: THREE.ColorRepresentation,
    count: number,
    instanceMatrix: THREE.InstancedBufferAttribute,
  ) {
    super(
      FeatureTopologyDisplayEdgesResourceFactory.createGeometry(snapshots),
      FeatureTopologyDisplayEdgesResourceFactory.createMaterial(color),
    )
    this.instanceMatrix = instanceMatrix
    this.count = count
    this.name = '__imported_model_instanced_topology_display_edges__'
    this.renderOrder = 1
    /** 实例包围需要合并全部 occurrence；首版关闭对象级裁剪以保证线框不会错误消失。 */
    this.frustumCulled = false
    this.userData = {
      ...this.userData,
      skipSelection: true,
      skipSelectionSubtree: true,
      isImportedModelInstancedTopologyDisplayEdges: true,
    }
  }

  /**
   * 更新默认显示边颜色。
   *
   * @param color 当前主题与显示模式要求的颜色。
   */
  public setDisplayEdgeColor(color: THREE.ColorRepresentation): void {
    const materials = Array.isArray(this.material) ? this.material : [this.material]
    materials.forEach((material) => {
      if (material instanceof THREE.LineBasicMaterial) {
        material.color.set(color)
        material.needsUpdate = true
      }
    })
  }

  /**
   * 默认显示边不参与 CPU 射线拾取。
   *
   * @param _raycaster Three.js 射线对象。
   * @param _intersects 命中输出数组。
   */
  public override raycast(
    _raycaster: THREE.Raycaster,
    _intersects: THREE.Intersection[],
  ): void {}

  /** 释放实例边线独占的 Geometry/Material；共享 instanceMatrix 由表面 Unit 持有。 */
  public dispose(): void {
    if (this.disposed) {
      return
    }
    this.disposed = true
    this.removeFromParent()
    this.geometry.dispose()
    const materials = Array.isArray(this.material) ? this.material : [this.material]
    materials.forEach(material => material.dispose())
    /** WebGL/WebGPU 属性管理器按 InstancedMesh 契约监听 dispose 事件清理对象级绑定。 */
    this.dispatchEvent({ type: 'dispose' } as never)
  }
}
